import { FC, useEffect, useMemo, useState } from 'react';
import {
  Grid, Divider, TextField, MenuItem, IconButton, Button, Chip, CircularProgress, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchBarcodePatterns, createBarcodePattern, updateBarcodePattern,
} from '../../store/barcodePatternStore/barcodePatternThunk';

interface Segment { token: string; arg?: string; }

// `batchOnly` parts need batch/date data that only exists at GRN — hidden for per-item codes.
// `textOnly` parts can contain letters — not allowed under EAN-13 (numeric only).
const SEGMENT_TYPES: { token: string; label: string; hasDateFmt?: boolean; hasDigits?: boolean; batchOnly?: boolean; textOnly?: boolean }[] = [
  { token: 'ITEMCODE', label: 'Item code' },
  { token: 'CATEGORY', label: 'Category', textOnly: true },
  { token: 'SEQ', label: 'Sequence', hasDigits: true },
  { token: 'MFGDATE', label: 'Mfg date', hasDateFmt: true, batchOnly: true },
  { token: 'EXPIRY', label: 'Expiry date', hasDateFmt: true, batchOnly: true },
  { token: 'BATCH', label: 'Batch number', batchOnly: true, textOnly: true },
  // Parked: Batch has no weight field and nothing captures one at GRN, so {WEIGHT}
  // always rendered empty. Quantity replaces it.
  // { token: 'WEIGHT', label: 'Weight', batchOnly: true },
  { token: 'QTY', label: 'Quantity', batchOnly: true },
  { token: 'LOC', label: 'Location', batchOnly: true, textOnly: true },
  { token: 'CHK', label: 'Check digit (EAN-13)' },
];

const DATE_FORMATS = ['YYMMDD', 'DDMMYY', 'YYYYMMDD', 'YYMM', 'MMYY'];
const SYMBOLOGIES = ['CODE128', 'EAN13'];

// Per-type default builders. internal_sku is static; internal_batch carries the mfg date.
const DEFAULTS: Record<string, { segments: Segment[]; separator: string; symbology: string }> = {
  internal_sku: { segments: [{ token: 'ITEMCODE' }, { token: 'SEQ', arg: '3' }], separator: '-', symbology: 'CODE128' },
  internal_batch: { segments: [{ token: 'ITEMCODE' }, { token: 'MFGDATE', arg: 'YYMMDD' }, { token: 'SEQ', arg: '3' }], separator: '-', symbology: 'CODE128' },
};

const typeOf = (token: string) => SEGMENT_TYPES.find((t) => t.token === token);
const labelOf = (token: string) => typeOf(token)?.label || token;

const buildTemplate = (segments: Segment[], separator: string): string =>
  segments
    .map((s) => {
      if (s.token === 'SEQ') return `{SEQ:${s.arg || '3'}}`;
      if (s.token === 'MFGDATE' || s.token === 'EXPIRY') return `{${s.token}:${s.arg || 'YYMMDD'}}`;
      return `{${s.token}}`;
    })
    .join(separator);

const parseTemplate = (template: string): { segments: Segment[]; separator: string } => {
  const tokenRe = /\{([A-Z]+)(?::([^}]+))?\}/g;
  const segments: Segment[] = [];
  const matches: { index: number; end: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(template)) !== null) {
    segments.push({ token: m[1], arg: m[2] });
    matches.push({ index: m.index, end: tokenRe.lastIndex });
  }
  let separator = '-';
  if (matches.length >= 2) separator = template.slice(matches[0].end, matches[1].index) || '-';
  return { segments, separator };
};

const previewSample: Record<string, string> = {
  ITEMCODE: 'TSH001', CATEGORY: 'APP', BATCH: 'B12', QTY: '24', LOC: 'WH1', CHK: '7',
};

// EAN-13 check digit for a 12-digit numeric string (mirrors the backend renderer).
const ean13CheckDigit = (digits12: string) => {
  const d = digits12.replace(/\D/g, '').padEnd(12, '0').slice(0, 12);
  let sum = 0;
  for (let i = 0; i < 12; i += 1) sum += Number(d[i]) * (i % 2 === 0 ? 1 : 3);
  return String((10 - (sum % 10)) % 10);
};
// Reshape numeric parts into a valid 13-digit EAN-13 (internal prefix 20 + pad + check digit).
const toEan13Preview = (raw: string) => {
  let d = raw.replace(/\D/g, '');
  if (!/^2\d/.test(d)) d = '20' + d;      // ensure an internal prefix (20–29)
  d = (d + '000000000000').slice(0, 12);  // pad/truncate to 12 data digits
  return d + ean13CheckDigit(d);
};

const buildPreview = (segments: Segment[], separator: string, symbology: string): string => {
  const isEan = symbology === 'EAN13';
  const now = new Date();
  const fmtDate = (fmt: string) => {
    const yyyy = String(now.getFullYear());
    const map: Record<string, string> = {
      YYYY: yyyy, YY: yyyy.slice(-2),
      MM: String(now.getMonth() + 1).padStart(2, '0'),
      DD: String(now.getDate()).padStart(2, '0'),
    };
    return fmt.replace(/YYYY|YY|MM|DD/g, (t) => map[t]);
  };
  const parts = segments
    .filter((s) => !(isEan && s.token === 'CHK')) // check digit is automatic for EAN-13
    .map((s) => {
      if (s.token === 'SEQ') return '1'.padStart(Number(s.arg) || 3, '0');
      if (s.token === 'MFGDATE' || s.token === 'EXPIRY') return fmtDate(s.arg || 'YYMMDD');
      // Item codes are usually numeric; use a numeric sample under EAN-13.
      if (s.token === 'ITEMCODE') return isEan ? '1100' : previewSample.ITEMCODE;
      return previewSample[s.token] ?? '';
    })
    .filter((v) => v !== '');
  // EAN-13 holds only digits — reshape into the real numeric code, not text.
  if (isEan) return toEan13Preview(parts.join(''));
  return parts.join(separator);
};

interface ModeState { patternId: string | null; segments: Segment[]; separator: string; symbology: string; }

const BarcodeSettings: FC = () => {
  const dispatch = useAppDispatch();
  const accountDetails = useAppSelector((state) => state.account.accountDetails);
  const patterns = useAppSelector((state) => state.barcodePatterns.patterns);

  const [activeMode, setActiveMode] = useState<'internal_sku' | 'internal_batch'>('internal_sku');
  const [byMode, setByMode] = useState<Record<string, ModeState>>({
    internal_sku: { patternId: null, ...DEFAULTS.internal_sku },
    internal_batch: { patternId: null, ...DEFAULTS.internal_batch },
  });
  const [addToken, setAddToken] = useState('');
  const [saving, setSaving] = useState(false);
  // A saved pattern is locked (read-only) until the user clicks Update.
  const [locked, setLocked] = useState(false);

  const cur = byMode[activeMode];
  const setCur = (patch: Partial<ModeState>) =>
    setByMode((prev) => ({ ...prev, [activeMode]: { ...prev[activeMode], ...patch } }));

  useEffect(() => {
    if (accountDetails?._id) dispatch(fetchBarcodePatterns('scope=account'));
  }, [accountDetails?._id, dispatch]);

  // Hydrate both per-type builders from the stored account patterns.
  useEffect(() => {
    const next = {
      internal_sku: { patternId: null, ...DEFAULTS.internal_sku } as ModeState,
      internal_batch: { patternId: null, ...DEFAULTS.internal_batch } as ModeState,
    };
    (patterns || []).forEach((p: any) => {
      if (p.scope !== 'account') return;
      const mode = p.barcodeMode === 'internal_batch' ? 'internal_batch' : 'internal_sku';
      const parsed = parseTemplate(p.template || '');
      next[mode] = {
        patternId: p._id,
        segments: parsed.segments.length ? parsed.segments : DEFAULTS[mode].segments,
        separator: parsed.separator,
        symbology: p.symbology || 'CODE128',
      };
    });
    setByMode(next);
    // Lock the type currently in view if it already has a saved pattern.
    setLocked(!!next[activeMode].patternId);
  }, [patterns]);

  // Switching type: lock if that type already has a saved pattern, else editable.
  useEffect(() => {
    setLocked(!!byMode[activeMode].patternId);
    setAddToken('');
  }, [activeMode]);

  const template = useMemo(() => buildTemplate(cur.segments, cur.separator), [cur.segments, cur.separator]);
  const preview = useMemo(() => buildPreview(cur.segments, cur.separator, cur.symbology), [cur.segments, cur.separator, cur.symbology]);
  const hasSeq = cur.segments.some((s) => s.token === 'SEQ');
  // Exclude batch-only parts for per-item codes, parts already added (no duplicates), and
  // the EAN-13 check digit unless the symbology is EAN13 (CODE128 has its own checksum).
  const usedTokens = new Set(cur.segments.map((s) => s.token));
  const availableParts = SEGMENT_TYPES.filter(
    (t) =>
      (activeMode === 'internal_batch' || !t.batchOnly) &&
      !usedTokens.has(t.token) &&
      (t.token !== 'CHK' || cur.symbology === 'EAN13') &&
      // EAN-13 is numeric-only — hide text parts (Category, Batch no., Location).
      !(cur.symbology === 'EAN13' && t.textOnly)
  );

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= cur.segments.length) return;
    const next = [...cur.segments];
    [next[i], next[j]] = [next[j], next[i]];
    setCur({ segments: next });
  };

  // Drag-and-drop reordering (native HTML5 DnD — no dependency).
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const reorder = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    const next = [...cur.segments];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setCur({ segments: next });
  };
  const removeSeg = (i: number) => setCur({ segments: cur.segments.filter((_, idx) => idx !== i) });
  const setArg = (i: number, arg: string) => setCur({ segments: cur.segments.map((s, idx) => (idx === i ? { ...s, arg } : s)) });
  const addSegment = (token: string) => {
    if (!token) return;
    const t = typeOf(token);
    const seg: Segment = { token };
    if (t?.hasDateFmt) seg.arg = 'YYMMDD';
    if (t?.hasDigits) seg.arg = '3';
    setCur({ segments: [...cur.segments, seg] });
    setAddToken('');
  };

  const onSave = async () => {
    if (!cur.segments.length) return;
    setSaving(true);
    try {
      const payload = { barcodeMode: activeMode, scope: 'account', template, symbology: cur.symbology };
      if (cur.patternId) await dispatch(updateBarcodePattern({ id: cur.patternId, payload })).unwrap();
      else await dispatch(createBarcodePattern(payload)).unwrap();
      dispatch(fetchBarcodePatterns('scope=account'));
      setLocked(true); // back to read-only after a successful save
    } catch {
      /* toast handled in slice; stay editable so the user can retry */
    } finally {
      setSaving(false);
    }
  };

  // Cancel edits: revert this type to its saved pattern (or defaults if unsaved) and re-lock.
  const onCancel = () => {
    const saved = (patterns || []).find(
      (p: any) => p.scope === 'account' && (p.barcodeMode === 'internal_batch' ? 'internal_batch' : 'internal_sku') === activeMode
    );
    if (saved) {
      const parsed = parseTemplate(saved.template || '');
      setByMode((prev) => ({
        ...prev,
        [activeMode]: {
          patternId: saved._id,
          segments: parsed.segments.length ? parsed.segments : DEFAULTS[activeMode].segments,
          separator: parsed.separator,
          symbology: saved.symbology || 'CODE128',
        },
      }));
      setLocked(true);
    } else {
      setByMode((prev) => ({ ...prev, [activeMode]: { patternId: null, ...DEFAULTS[activeMode] } }));
    }
    setAddToken('');
  };

  return (
    <div className={'c-card2'} style={{ padding: 15, paddingTop: 10, paddingBottom: 15, maxWidth: 720 }}>
      <h5 style={{ fontWeight: 'bold', color: '#000', fontSize: 15, marginBottom: 10 }}>Barcode Format</h5>
      <Divider sx={{ mt: 0.5, mb: 1.5 }} />
      <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 13, marginBottom: 12 }}>
        Each internal barcode type has its own format. <strong>Per item</strong> is static (item code + sequence);
        <strong> per batch</strong> can include mfg/expiry/batch parts that are filled in at goods receipt.
        Manufacturer barcodes are captured as-is and ignore these formats.
      </h5>

      <ToggleButtonGroup
        size="small" exclusive value={activeMode}
        onChange={(_e, v) => { if (v) { setActiveMode(v); setAddToken(''); } }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="internal_sku">Per item</ToggleButton>
        <ToggleButton value="internal_batch">Per batch</ToggleButton>
      </ToggleButtonGroup>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <TextField select fullWidth size="small" label="Symbology" value={cur.symbology} disabled={locked}
            onChange={(e) => setCur({ symbology: e.target.value })}>
            {SYMBOLOGIES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField fullWidth size="small" label="Separator" value={cur.separator} disabled={locked}
            onChange={(e) => setCur({ separator: e.target.value })} inputProps={{ maxLength: 3 }} />
        </Grid>
      </Grid>

      <h5 style={{ fontWeight: 600, color: '#000', fontSize: 13, marginBottom: 8 }}>Code parts (top → bottom = left → right)</h5>

      {cur.segments.map((seg, i) => {
        const t = typeOf(seg.token);
        return (
          <div
            key={`${seg.token}-${i}`}
            onDragOver={(e) => { if (dragIndex !== null) e.preventDefault(); }}
            onDrop={() => { if (dragIndex !== null) reorder(dragIndex, i); setDragIndex(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginBottom: 6,
              background: dragIndex === i ? '#eef2f7' : '#f7f8fa', borderRadius: 8,
              opacity: dragIndex === i ? 0.5 : 1,
              borderTop: dragIndex !== null && dragIndex !== i ? '2px dashed transparent' : undefined,
            }}
          >
            <span
              draggable={!locked}
              onDragStart={() => setDragIndex(i)}
              onDragEnd={() => setDragIndex(null)}
              style={{ display: 'flex', cursor: locked ? 'default' : 'grab' }}
            >
              <DragIndicatorIcon fontSize="small" style={{ color: locked ? '#dfe3e8' : '#b0b8c4' }} />
            </span>
            <span style={{ flex: 1, fontSize: 14, color: '#000' }}>{labelOf(seg.token)}</span>

            {t?.hasDateFmt ? (
              <TextField select size="small" variant="standard" value={seg.arg || 'YYMMDD'} disabled={locked}
                onChange={(e) => setArg(i, e.target.value)} sx={{ width: 110 }}>
                {DATE_FORMATS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
            ) : null}

            {t?.hasDigits ? (
              <TextField size="small" variant="standard" type="number" label="digits" value={seg.arg || '3'} disabled={locked}
                onChange={(e) => setArg(i, e.target.value)} sx={{ width: 64 }} inputProps={{ min: 1, max: 9 }} />
            ) : null}

            {seg.token === 'CHK' && cur.symbology !== 'EAN13' ? (
              <Tooltip title="Check digit only applies to EAN-13"><Chip size="small" color="warning" label="EAN-13 only" /></Tooltip>
            ) : null}

            <IconButton size="small" onClick={() => move(i, -1)} disabled={locked || i === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => move(i, 1)} disabled={locked || i === cur.segments.length - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
            <IconButton size="small" onClick={() => removeSeg(i)} disabled={locked}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </div>
        );
      })}

      {!locked ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, marginBottom: 14 }}>
          <TextField select size="small" value={addToken} onChange={(e) => setAddToken(e.target.value)}
            sx={{ minWidth: 200 }} label="Add a part">
            {availableParts.map((t) => <MenuItem key={t.token} value={t.token}>{t.label}</MenuItem>)}
          </TextField>
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => addSegment(addToken)} disabled={!addToken}>Add</Button>
        </div>
      ) : null}

      {!hasSeq ? (
        <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>
          Tip: without a Sequence part, generated codes can collide. Add “Sequence” for uniqueness.
        </div>
      ) : null}

      <Divider sx={{ my: 1.5 }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 12, color: '#9da7b7' }}>Preview ({activeMode === 'internal_batch' ? 'per batch' : 'per item'})</div>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 1, fontFamily: 'monospace' }}>{preview || '—'}</div>
          <div style={{ fontSize: 11, color: '#b0b8c4', marginTop: 2 }}>Template: <code>{template}</code></div>
        </div>
        {locked ? (
          <Button variant="outlined" onClick={() => setLocked(false)}
            sx={{ color: '#059669', borderColor: '#059669', '&:hover': { borderColor: '#047857' } }}>
            Update
          </Button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            {cur.patternId ? (
              <Button variant="outlined" color="inherit" onClick={onCancel} disabled={saving}
                sx={{ color: '#7a8290', borderColor: '#cdd3db' }}>
                Cancel
              </Button>
            ) : null}
            <Button variant="contained" onClick={onSave} disabled={saving || !cur.segments.length}
              sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
              {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarcodeSettings;
